import { cloneElement, useCallback, useRef, useState } from 'react';
import { Box, Tooltip, Text, Button, useToken, Portal } from '@bitrise/bitkit';
import { useDebounceValue, useEventListener, useOnClickOutside } from 'usehooks-ts';

type HighlightTipsProps = {
  visible: boolean;
  message: string;
  title: string;
  onClose: () => void;
  children: JSX.Element;
};

export default ({ visible, message, title, onClose, children }: HighlightTipsProps): JSX.Element => {
  const highlightedCompRef = useRef<HTMLElement>(null);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [debouncedRect] = useDebounceValue(rect, 50);
  const borderColor = useToken('colors', 'purple.50', '#9247C2');
  const originalCompRef = useCallback((node: HTMLElement) => {
    if (node) {
      setRect(node.getBoundingClientRect());
    }
  }, []);
  useOnClickOutside(highlightedCompRef, onClose);

  useEventListener('resize', () => {
    if (debouncedRect) {
      setRect(null);
    }
  });

  if (!visible || !debouncedRect) {
    return cloneElement(children, { ref: originalCompRef });
  }

  const cloned = cloneElement(children, {
    ref: highlightedCompRef,
    style: {
      position: 'absolute',
      top: debouncedRect.y,
      left: debouncedRect.x,
      border: `3px solid ${borderColor}`,
    },
  });

  return (
    <Box w={rect?.width} h={rect?.height}>
      <Portal>
        <Box
          pos="fixed"
          w="100vw"
          h="100vh"
          top="0"
          right="0"
          bottom="0"
          left="0"
          bg="rgba(0,0,0,.2)"
          data-testid="highlight-tips-backdrop"
          zIndex={101}
        >
          <Tooltip
            isOpen
            bg="neutral.100"
            textColor="black"
            placement="bottom-start"
            maxW="338px"
            mt="8px"
            p="20"
            portalProps={{ appendToParentPortal: true }}
            label={
              <Box gap="12">
                <Text fontWeight="bold" mb="0.5rem">
                  {title}
                </Text>
                <Text>{message}</Text>
                <Box display="flex" justifyContent="end">
                  <Button size="sm" mt="0.8rem" onClick={onClose}>
                    Got it
                  </Button>
                </Box>
              </Box>
            }
          >
            {cloned}
          </Tooltip>
        </Box>
      </Portal>
    </Box>
  );
};
